/**
 * Address check against cl=addressvalidator, as a hook before the form is sent.
 *
 * Markup contract: inside the validated form, around the message block
 * (ajax/addresscheck_messageblock.html.twig).
 *   <div x-data="addressCheck" x-show="showMessage" …>
 *
 * The hook of validatedForm awaits the request before the form goes out. A reply the
 * customer has to answer keeps the form open and shows the corrected address; every
 * other reply lets the submit continue. The answer is written to dd_addresscheck_status, which the server reads and
 * which keeps a second submit from asking again.
 */
import { postAction } from '../shell/request.js';

// statuses the customer never sees: the check found nothing to correct, or it could not run
const PASSING = ['error', 'not-checked', 'valid', 'inactive'];

// the service answers with html entities in the corrected parts
function decode(value) {
    const element = document.createElement('textarea');
    element.innerHTML = value ?? '';

    return element.value;
}

document.addEventListener('alpine:init', () => {
    Alpine.data('addressCheck', () => ({
        showMessage: false,
        mode: '',
        address: {},

        init() {
            this.form = this.$el.closest('form');
            this.isDeliveryAddress = false;
            this.$data.addBeforeSubmit(() => this.run());
        },

        /** Only a home delivery is checked; a packstation or a store has no street to correct. */
        get applies() {
            const status = this.form.querySelector('[name="dd_addresscheck_status"]');
            if (status && status.value !== 'zipnotfound') {
                return false;
            }

            const chosen = this.form.querySelector('input[name="oxaddressid"]:checked');

            return !chosen || chosen.closest('#homeAddressesBox') !== null;
        },

        async run() {
            if (!this.applies) {
                return true;
            }

            let content;
            try {
                const answer = await postAction(
                    `/${URLSEGMENT}index.php?cl=addressvalidator&fnc=doaddressvalidation`, this.payload());
                content = answer.content;
            } catch (error) {
                this.setStatus('not-checked');
                return true;
            }

            this.setStatus(content.status);

            if (PASSING.includes(content.status)) {
                return true;
            }
            // a correction the customer cannot judge is no correction
            if (content.status === 'invalid' && !this.isComplete(content.oAddressCheck)) {
                return true;
            }

            this.address = content.oAddressCheck;
            this.isDeliveryAddress = content.isdeladdr == 1;
            this.markChangedFields(content);
            this.mode = content.status;
            this.placeAtAddress();
            this.showMessage = true;
            // x-show unhides the block only on the next tick; a hidden element does not scroll
            this.$nextTick(() => this.$el.scrollIntoView({ block: 'center', behavior: 'smooth' }));

            return false;
        },

        /** The message goes above the street of the checked address, the form around it opens. */
        placeAtAddress() {
            const prefix = this.isDeliveryAddress ? 'deladr[oxaddress__ox' : 'invadr[oxuser__ox';
            const street = [...this.form.querySelectorAll(`[name="${prefix}street]"]`)]
                .find((field) => !field.disabled && !field.closest('fieldset:disabled'));
            if (!street) {
                return;
            }

            if (!street.checkVisibility()) {
                street.dispatchEvent(new CustomEvent('reveal-field', { bubbles: true }));
            }
            street.closest('.ddhes-form--el')?.before(this.$el);
        },

        payload() {
            const body = new URLSearchParams();
            this.form.querySelectorAll('input:not([type=hidden]), select').forEach((field) => {
                // a field in a disabled fieldset is not submitted, but its own disabled stays false
                if (field.disabled || field.closest('fieldset:disabled')
                    || !field.name || ['lgn_pwd', 'lgn_usr'].includes(field.name)) {
                    return;
                }
                if ((field.type === 'checkbox' || field.type === 'radio') && !field.checked) {
                    return;
                }
                body.append(field.name, field.value);
            });

            return body;
        },

        isComplete(checked) {
            return ['checked_street', 'checked_streetnr', 'checked_zip', 'checked_city']
                .every((key) => checked[key] !== null);
        },

        markChangedFields({ status, isdeladdr, changes }) {
            if (status !== 'corrected') {
                return;
            }

            const prefix = isdeladdr == 1 ? 'deladr[oxaddress__ox' : 'invadr[oxuser__ox';
            ['street', 'streetnr', 'zip', 'city'].forEach((name) => {
                if (!changes[name]) {
                    return;
                }
                this.form.querySelector(`[name="${prefix}${name}]"]`)
                    ?.closest('.ddhes-form--el')?.classList.add('checkFailed');
            });
        },

        applyCorrection() {
            const prefix = this.isDeliveryAddress ? 'deladr[oxaddress__ox' : 'invadr[oxuser__ox';
            ['street', 'streetnr', 'zip', 'city'].forEach((name) => {
                const field = this.form.querySelector(`[name="${prefix}${name}]"]`);
                if (field) {
                    field.value = decode(this.address[`checked_${name}`]);
                }
            });

            this.setStatus('applied');
            this.continueSubmit();
        },

        keepAddress() {
            this.setStatus('invalid-confirmed');
            this.continueSubmit();
        },

        dismiss(status) {
            this.setStatus(status);
            this.showMessage = false;
        },

        continueSubmit() {
            this.showMessage = false;
            this.form.requestSubmit();
        },

        setStatus(status) {
            let field = this.form.querySelector('[name="dd_addresscheck_status"]');
            if (!field) {
                field = document.createElement('input');
                field.type = 'hidden';
                field.name = 'dd_addresscheck_status';
                this.form.append(field);
            }
            field.value = status;
        },
    }));
});
